import React from 'react';
import { logger } from '@fiverr-private/obs';
import { translate } from '@fiverr-private/i18n-react';
import { Container, Stack } from '@fiverr-private/layout_components';
import { ErrorBoundary } from '@fiverr-private/ui_utils';
import { Heading } from '@fiverr-private/typography';
import { useSellerData, SellerPageFederatedSeller } from '@fiverr-private/seller_pages_toolbox';
import { SellerRole } from '../../../gql-operations/gql-generated';
import { useRoleVariation } from '../../hooks/useRoleVariation';
import { useFetchReviewsOnRoleSwitch } from '../../hooks/useFetchReviewsOnRoleSwitch';
import { useUGCData } from '../../hooks/useUGCData';
import { useShouldRenderTraderDetailsButton } from '../../hooks/useShouldRenderTraderDetailsButton';
import { translateRoleTitle } from '../../utils/translateRoleTitle';
import { StickyHeader } from '../StickyHeader';
import { RoleTabReporter } from '../RoleTabReporter';
import { RolePageHighlights } from '../RolePageHighlights';
import { Description } from '../Description';
import { Highlights } from '../Highlights';
import { HighlightedReviews } from '../HighlightedReviews/HighlightedReviews';
import { RolePageSkills } from '../RolePageSkills';
import { Expertise } from '../Expertise/Expertise';
import { LazyRolePageClients } from '../RolePageClients/LazyRolePageClients';
import { GigServices } from '../GigServices';
import { RolePagePortfolio } from '../RolePagePortfolio';
import { WorkExperience } from '../WorkExperience';
import { EducationSection } from '../EducationSection/EducationSection';
import { FAQSection } from '../FAQSection';
import { RolePageReviews } from '../RolePageReviews';
import { RolePageFreelancerDetails } from '../RolePageFreelancerDetails/RolePageFreelancerDetails';
import { RolePageTestimonials } from '../RolePageTestimonials';
import { ProSection } from '../ProSection';
import { createTabId } from '../StickyHeader/tab-names';
import { RolePageTabContentRoleProvider } from '../RolePageTabContentRoleProvider';
import { ServicesAndSkills } from '../ServicesAndSkills';

interface RolePageTabContentProps {
    sellerRole: SellerRole | null;
    hideTitle?: boolean;
}

export const RolePageTabContent: React.FC<RolePageTabContentProps> = ({ sellerRole, hideTitle }) => {
    const { isPro, courses, activeEducations, certifications, sellerId, sellerName, testimonials } =
        useSellerData(selectSellerSlice);
    const roleVariation = useRoleVariation();
    const { areReviewsLoading } = useFetchReviewsOnRoleSwitch();
    const { showTranslatedUGC } = useUGCData();

    const shouldRenderProSection = sellerRole ? sellerRole.pro : isPro;
    const shouldRenderEduContent = Boolean(activeEducations.length || certifications.length || courses?.nodes?.length);
    const shouldRenderFreelancerDetailsButton = useShouldRenderTraderDetailsButton();
    const shouldRenderTestimonials = testimonials && testimonials.length > 0;

    const roleTabTitle = sellerRole
        ? translateRoleTitle(sellerRole, roleVariation[sellerRole.id])
        : translate('seller_page_perseus.professions_page.roles_tabs.overview');

    return (
        <ErrorBoundary onError={onRenderError}>
            <RolePageTabContentRoleProvider sellerRole={sellerRole}>
                <RoleTabReporter sellerRoleId={sellerRole?.id} />
                <Stack direction="column" gap="14" paddingBottom="14">
                    <Stack direction="column" gap="8">
                        <Stack direction="column" gap={{ default: '8', lg: '10' }}>
                            {!hideTitle && (
                                <Heading
                                    as="h1"
                                    size={{ default: 'h_lg', sm: 'h_xl', lg: 'h_xxl' }}
                                    paddingTop={{ default: '8', lg: '10' }}
                                    paddingBottom="0"
                                >
                                    {roleTabTitle}
                                </Heading>
                            )}
                            {shouldRenderProSection ? (
                                <ProSection sellerRoleId={sellerRole?.id} />
                            ) : (
                                <RolePageHighlights sellerRoleId={sellerRole?.id} />
                            )}
                        </Stack>
                        <Description sellerRoleId={sellerRole?.id} />
                        <Container hidden={{ default: false, lg: true }}>
                            <Highlights />
                        </Container>
                        <HighlightedReviews areReviewsLoading={areReviewsLoading} />
                    </Stack>

                    {sellerRole && <ServicesAndSkills />}
                    <GigServices sellerRoleId={sellerRole?.id} showTranslatedUGC={showTranslatedUGC} />

                    {sellerRole ? <RolePageSkills /> : <Expertise />}

                    <LazyRolePageClients />

                    <WorkExperience />
                    <RolePagePortfolio sellerRoleId={sellerRole?.id} />
                    {shouldRenderEduContent && <EducationSection />}
                    <FAQSection sellerRoleId={sellerRole?.id} />
                    <Stack direction="column" gap="8">
                        <RolePageReviews
                            sellerRoleId={sellerRole?.id}
                            withBottomDivider={shouldRenderFreelancerDetailsButton || shouldRenderTestimonials}
                            areReviewsLoading={areReviewsLoading}
                        />
                        {shouldRenderFreelancerDetailsButton && (
                            <RolePageFreelancerDetails sellerId={sellerId} sellerName={sellerName} />
                        )}
                        {shouldRenderTestimonials && (
                            <div
                                id={createTabId({
                                    tabId: StickyHeader.TabName.Testimonials,
                                    roleId: sellerRole?.id,
                                })}
                            >
                                <RolePageTestimonials />
                            </div>
                        )}
                    </Stack>
                </Stack>
            </RolePageTabContentRoleProvider>
        </ErrorBoundary>
    );
};

const selectSellerSlice = (seller: SellerPageFederatedSeller) => ({
    isPro: seller.isPro,
    courses: seller.courses,
    activeEducations: seller.activeEducations,
    certifications: seller.certifications,
    sellerId: seller.user.id,
    sellerName: seller.user.name,
    testimonials: seller.testimonials,
});

const onRenderError = (originalError) => {
    const err = new Error('Failed while rendering the MainContent Component');
    logger.error(err, {
        cause: originalError,
    });
};
